import { View, CollectionView, getOption } from "backbone.marionette";
import template from "templates/course_map/evaluations_dashboard.hbs";
import DashboardCard from "views/course_map/dashboard_card";
import ModalErrorView from "views/modal/modal_error";

const DEFAULT_CARD_PAGE_SIZE = 15;
const CARD_PAGE_SIZE_OPTIONS = [15, 30, 60];

const PROJECT_TYPES = {
  UserProject: {
    label: "Individual",
    creatorTypes: ["User"],
  },
  GroupProject: {
    label: "Group",
    creatorTypes: ["Group"],
  },
  OpenProject: {
    label: "Individual & Group",
    creatorTypes: ["User", "Group"],
  },
};
const DEFAULT_PROJECT_TYPE = PROJECT_TYPES.UserProject;

const uniq = (values) => [...new Set(values)];

export default class EvaluationsDashboard extends View.extend({
  template,
  ui: {
    projectTab: "[data-project-tab]",
    creatorTab: "[data-creator-tab]",
  },
  regions: {
    cards: '[data-region="cards"]',
  },
  events: {
    "click [data-project-tab]": "onProjectTabClick",
    "click [data-creator-tab]": "onCreatorTabClick",
    "click [data-page-control]": "onPageControlClick",
    "change [data-page-size]": "onPageSizeChange",
  },
}) {
  initialize(options) {
    this.options = options || {};
    this.courseId = getOption(this, "courseId");
    this.collections = getOption(this, "collections");

    this.projects = this._visibleProjects();
    this.students = this.collections.user.models;
    this.groups = this.collections.group.models;

    // Default selection
    const firstProject = this.projects[0];
    this.selectedProjectId = firstProject ? firstProject.id : null;
    this.selectedCreatorId = null;
    this.selectedCreatorType = null;

    this.cardPage = 0;
    this.cardPageSize = DEFAULT_CARD_PAGE_SIZE;

    this.projectsOpen = true;
    this.studentsOpen = false;
    this.groupsOpen = false;

    // Submission keys (creatorType-creatorId-projectId) we've already requested,
    // so the post-fetch re-render doesn't trigger another fetch.
    this.requestedKeys = new Set();
    this.unloading = false;
    window.addEventListener("beforeunload", () => {
      this.unloading = true;
    });

    this._resizeTimer = null;
    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        if (!this.isDestroyed()) this._reserveGridHeight();
      }, 150);
    };
    window.addEventListener("resize", this._onResize);
  }

  // ---- Selection -------------------------------------------------------

  onProjectTabClick(event) {
    const id = parseInt(event.currentTarget.dataset.projectId, 10);
    if (Number.isNaN(id)) return;
    if (id === this.selectedProjectId && this.selectedCreatorId === null)
      return;
    this.selectedProjectId = id;
    this.selectedCreatorId = null;
    this.selectedCreatorType = null;
    this.cardPage = 0;
    this.render();
    this._scrollToTop();
  }

  onCreatorTabClick(event) {
    const { creatorId, creatorType } = event.currentTarget.dataset;
    const id = parseInt(creatorId, 10);
    if (Number.isNaN(id)) return;
    if (
      id === this.selectedCreatorId &&
      creatorType === this.selectedCreatorType
    )
      return;
    this.selectedCreatorId = id;
    this.selectedCreatorType = creatorType;
    this.selectedProjectId = null;
    this.cardPage = 0;
    this.render();
    this._scrollToTop();
  }

  _scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  onPageControlClick(event) {
    event.preventDefault();
    const delta = event.currentTarget.dataset.pageDir === "next" ? 1 : -1;
    this.cardPage += delta;
    this.render();
  }

  onPageSizeChange(event) {
    const size = parseInt(event.currentTarget.value, 10);
    if (Number.isNaN(size) || size === this.cardPageSize) return;
    this.cardPageSize = size;
    this.cardPage = 0;
    this.render();
  }

  // ---- Render ----------------------------------------------------------

  onRender() {
    // The fixed .page-header height varies (and exceeds the $headerHeight SCSS
    // constant once the sub-nav row is included). Measure it so the sticky tab
    // rail aligns flush with whatever's actually rendered.
    const header = document.querySelector(".page-header");
    if (header) {
      const offset = header.getBoundingClientRect().height;
      this.el.style.setProperty("--dashboard-sticky-top", `${offset}px`);
    }

    this.el.querySelectorAll("[data-dashboard-section]").forEach((el) => {
      el.addEventListener("toggle", () => {
        const name = el.dataset.dashboardSection;
        if (name === "projects") this.projectsOpen = el.open;
        if (name === "students") this.studentsOpen = el.open;
        if (name === "groups") this.groupsOpen = el.open;
      });
    });

    this._showCards();

    // Reserve a full page's worth of grid height so a short last page doesn't
    // shift the footer/page upward.
    this._reserveGridHeight();

    // Fetch only the submissions for the cards currently on screen.
    this._loadVisibleSubmissions();
  }

  _showCards() {
    if (!this.pageCardModels || !this.pageCardModels.length) return;
    this.showChildView(
      "cards",
      new CollectionView({
        tagName: "ul",
        className: "dashboard-grid",
        childView: DashboardCard,
        collection: new Backbone.Collection(this.pageCardModels),
        childViewOptions: {
          collections: this.collections,
          courseId: this.courseId,
          isCreatorMode: this.selectedCreatorId !== null,
        },
      }),
    );
  }

  onDestroy() {
    window.removeEventListener("resize", this._onResize);
    clearTimeout(this._resizeTimer);
  }

  _reserveGridHeight() {
    const grid = this.el.querySelector(".dashboard-grid");
    if (!grid) return;
    const cards = grid.querySelectorAll("[data-card]");
    if (!cards.length) {
      grid.style.minHeight = "";
      return;
    }
    const styles = window.getComputedStyle(grid);
    const cols =
      styles.gridTemplateColumns.split(" ").filter(Boolean).length || 1;
    const rowGap = parseFloat(styles.rowGap) || 0;
    let cardHeight = 0;
    cards.forEach((card) => {
      cardHeight = Math.max(cardHeight, card.getBoundingClientRect().height);
    });
    const total = this.cardTotal != null ? this.cardTotal : cards.length;
    const reserveCount = Math.min(this.cardPageSize, total);
    const rows = Math.ceil(reserveCount / cols);
    const minHeight = rows * cardHeight + Math.max(0, rows - 1) * rowGap;
    grid.style.minHeight = `${minHeight}px`;
  }

  serializeData() {
    const isCreatorMode = this.selectedCreatorId !== null;

    const projectGroups = this._groupProjectsByType(isCreatorMode);

    const serializeCreators = (models, creatorType) =>
      models.map((creator) => ({
        id: creator.id,
        name: creator.get("name"),
        creatorType,
        selected:
          creator.id === this.selectedCreatorId &&
          creatorType === this.selectedCreatorType,
      }));

    const cardModels = this._currentCardModels();
    const cardsPage = this._paginate(
      cardModels,
      this.cardPage,
      this.cardPageSize,
    );
    this.cardPage = cardsPage.page;
    this.cardTotal = cardsPage.total;
    this.pageCardModels = cardsPage.items;

    return {
      projectGroups,
      students: serializeCreators(this.students, "User"),
      groups: serializeCreators(this.groups, "Group"),
      projectCount: this.projects.length,
      studentCount: this.students.length,
      groupCount: this.groups.length,
      hasStudents: this.students.length > 0,
      hasGroups: this.groups.length > 0,
      projectsOpen: this.projectsOpen,
      studentsOpen: this.studentsOpen,
      groupsOpen: this.groupsOpen,
      isCreatorMode,
      paneHeader: this._paneHeader(isCreatorMode),
      portfolioLink: this._portfolioLink(isCreatorMode),
      hasCards: cardsPage.items.length > 0,
      emptyMessage: this._emptyMessage(isCreatorMode),
      cardPager: this._pagerData(cardsPage, "cards"),
      cardPageSizeOptions: CARD_PAGE_SIZE_OPTIONS.map((value) => ({
        value,
        selected: value === this.cardPageSize,
      })),
    };
  }

  _creatorSegment(creatorType) {
    return creatorType === "Group" ? "groups" : "users";
  }

  _portfolioLink(isCreatorMode) {
    if (isCreatorMode) {
      const segment = this._creatorSegment(this.selectedCreatorType);
      return `/courses/${this.courseId}/${segment}/evaluations/creator/${this.selectedCreatorId}`;
    }
    const project = this._findProject(this.selectedProjectId);
    if (!project) return null;
    const segment = project.get("type") === "GroupProject" ? "groups" : "users";
    return `/courses/${this.courseId}/${segment}/evaluations/project/${project.id}`;
  }

  _paneHeader(isCreatorMode) {
    if (isCreatorMode) {
      const creator = this._findCreator(
        this.selectedCreatorId,
        this.selectedCreatorType,
      );
      if (!creator) return null;
      const count = this._projectsForCreatorType(this.selectedCreatorType)
        .length;
      const noun = this.selectedCreatorType === "Group" ? "Group" : "Student";
      return {
        eyebrow: noun,
        title: creator.get("name"),
        meta: `${count} ${count === 1 ? "project" : "projects"}`,
        viewLabel: `View ${noun}`,
      };
    }
    const project = this._findProject(this.selectedProjectId);
    if (!project) return null;
    const meta = this._projectMeta(project);
    const due = project.get("due_date");
    const counts = meta.creatorTypes.map((creatorType) =>
      this._creatorCountLabel(creatorType),
    );
    return {
      eyebrow: `${meta.label} Project`,
      title: project.get("name"),
      meta: `${due ? `Due ${due} · ` : ""}${counts.join(", ")}`,
      viewLabel: "View Project",
    };
  }

  _creatorCountLabel(creatorType) {
    const count = this._creatorsFor(creatorType).length;
    const singular = creatorType === "Group" ? "group" : "student";
    return `${count} ${count === 1 ? singular : `${singular}s`}`;
  }

  _emptyMessage(isCreatorMode) {
    if (isCreatorMode) {
      return this.selectedCreatorType === "Group"
        ? "This group has no projects yet."
        : "This student has no projects yet.";
    }
    const project = this._findProject(this.selectedProjectId);
    const creatorTypes = project
      ? this._projectMeta(project).creatorTypes
      : DEFAULT_PROJECT_TYPE.creatorTypes;
    const nouns = creatorTypes.map((creatorType) =>
      creatorType === "Group" ? "groups" : "students",
    );
    return `No ${nouns.join(" or ")} have been added to this project yet.`;
  }

  _currentCardModels() {
    if (this.selectedCreatorId !== null) {
      const creatorType = this.selectedCreatorType;
      const creator = this._findCreator(this.selectedCreatorId, creatorType);
      if (!creator) return [];
      return this._projectsForCreatorType(creatorType).map((project) => ({
        project,
        creator,
        creatorType,
      }));
    }
    const project = this._findProject(this.selectedProjectId);
    if (!project) return [];
    return this._projectMeta(project).creatorTypes.flatMap((creatorType) =>
      this._creatorsFor(creatorType).map((creator) => ({
        project,
        creator,
        creatorType,
      })),
    );
  }

  // ---- Lazy, visible-only submission loading ---------------------------

  _loadVisibleSubmissions() {
    const visible = this._paginate(
      this._currentCardModels(),
      this.cardPage,
      this.cardPageSize,
    ).items;

    const missing = visible.filter(
      ({ project, creator, creatorType }) =>
        !this.requestedKeys.has(this._key(creatorType, creator.id, project.id)),
    );
    if (missing.length === 0) return;

    let errorShown = false;
    let succeeded = false;
    let pending = 0;

    const settle = () => {
      pending -= 1;
      // Only re-render once everything has landed, and never after a total
      // failure — re-rendering re-enters this method and would loop.
      if (pending > 0 || !succeeded || this.isDestroyed()) return;
      this.render();
    };

    ["User", "Group"].forEach((creatorType) => {
      const batch = missing.filter((m) => m.creatorType === creatorType);
      if (batch.length === 0) return;
      const creatorParam = this._creatorSegment(creatorType);
      pending += 1;
      this.collections.submission
        .fetch({
          remove: false,
          data: {
            course: this.courseId,
            projects: uniq(batch.map(({ project }) => project.id)),
            [creatorParam]: uniq(batch.map(({ creator }) => creator.id)),
          },
          success: () => {
            succeeded = true;
            batch.forEach(({ project, creator }) =>
              this.requestedKeys.add(
                this._key(creatorType, creator.id, project.id),
              ),
            );
          },
          error: () => {
            if (this.unloading || errorShown) return;
            errorShown = true;
            Radio.channel("app").trigger(
              "modal:open",
              new ModalErrorView({
                vent: this,
                message:
                  "Exception: Unable to fetch collection models. Please report this error to your VOCAT administrator.",
              }),
            );
          },
        })
        .always(settle);
    });
  }

  _key(creatorType, creatorId, projectId) {
    return `${creatorType}-${creatorId}-${projectId}`;
  }

  // ---- Collections / lookups -------------------------------------------
  _visibleProjects() {
    return this.collections.project.filter((model) => {
      const abilities = model.get("abilities") || {};
      return abilities.can_show_submissions === true;
    });
  }

  _projectMeta(project) {
    return PROJECT_TYPES[project.get("type")] || DEFAULT_PROJECT_TYPE;
  }

  _groupProjectsByType(isCreatorMode) {
    const sections = Object.values(PROJECT_TYPES).map((meta) => ({
      label: meta.label,
      projects: [],
    }));
    const byLabel = new Map(sections.map((section) => [section.label, section]));

    this.projects.forEach((project) => {
      byLabel.get(this._projectMeta(project).label).projects.push({
        id: project.id,
        name: project.get("name"),
        due_date: project.get("due_date"),
        selected: !isCreatorMode && project.id === this.selectedProjectId,
      });
    });

    const populated = sections.filter((section) => section.projects.length > 0);
    return populated.map((section) => ({
      ...section,
      showDivider: populated.length > 1,
    }));
  }

  _projectsForCreatorType(creatorType) {
    return this.projects.filter((project) =>
      this._projectMeta(project).creatorTypes.includes(creatorType),
    );
  }

  _creatorsFor(creatorType) {
    return creatorType === "Group" ? this.groups : this.students;
  }

  _findProject(id) {
    return this.projects.find((p) => p.id === id);
  }

  _findCreator(id, creatorType) {
    return this._creatorsFor(creatorType).find((c) => c.id === id);
  }

  // ---- Pagination helpers ----------------------------------------------

  _paginate(items, page, size) {
    const pageCount = Math.max(1, Math.ceil(items.length / size));
    const clamped = Math.min(Math.max(page, 0), pageCount - 1);
    const start = clamped * size;
    return {
      items: items.slice(start, start + size),
      page: clamped,
      pageCount,
      start,
      size,
      total: items.length,
    };
  }

  _pagerData(p, target) {
    return {
      target,
      humanPage: p.page + 1,
      pageCount: p.pageCount,
      hasPrev: p.page > 0,
      hasNext: p.page < p.pageCount - 1,
      rangeStart: p.total === 0 ? 0 : p.start + 1,
      rangeEnd: Math.min(p.start + p.size, p.total),
      total: p.total,
      showPager: p.pageCount > 1,
    };
  }
}
